import { useEffect, useRef, useState } from 'react'
import { Mars, Users, Venus } from 'lucide-react'
import type { ProductAudience } from '@/lib/productAudience'
import {
  productAudienceCompactLabel,
  productAudienceDescription,
  productAudienceLabel,
} from '@/lib/productAudience'
import { cn } from '@/lib/utils'

interface ProductAudienceBadgeProps {
  audience: ProductAudience
  className?: string
  /** Pin to product image — popup stays inside image bounds. */
  placement?: 'default' | 'on-image' | 'inline'
}

function AudienceSymbol({
  audience,
  compact,
}: {
  audience: ProductAudience
  compact?: boolean
}) {
  const iconClass = cn(
    'shrink-0',
    compact ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5'
  )

  if (audience === 'both') {
    return <Users className={iconClass} strokeWidth={2.25} aria-hidden />
  }
  if (audience === 'men') {
    return <Mars className={iconClass} strokeWidth={2.25} aria-hidden />
  }
  return <Venus className={iconClass} strokeWidth={2.25} aria-hidden />
}

/** Storefront badge for optional men / women / both targeting. */
export function ProductAudienceBadge({
  audience,
  className,
  placement = 'default',
}: ProductAudienceBadgeProps) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const label = productAudienceLabel(audience)
  const compactLabel = productAudienceCompactLabel(audience)
  const description = productAudienceDescription(audience)
  const onImage = placement === 'on-image'
  const inline = placement === 'inline'

  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current?.contains(event.target as Node)) return
      setOpen(false)
    }

    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [open])

  useEffect(() => {
    if (!open) return

    const timer = window.setTimeout(() => setOpen(false), 3000)
    return () => window.clearTimeout(timer)
  }, [open])

  const toggleOpen = (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setOpen((current) => !current)
  }

  const button = (
    <button
      type="button"
      aria-label={label}
      aria-expanded={open}
      onClick={toggleOpen}
      className={cn(
        inline
          ? 'product-card-audience-badge'
          : 'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/10 bg-black/50 text-gold-200 backdrop-blur-sm transition-colors hover:bg-black/65 hover:text-gold-100',
        open && !inline && 'bg-black/70 text-gold-100 ring-1 ring-gold-500/40',
        open &&
          inline &&
          'border-gold-500/45 bg-gold-100/90 ring-1 ring-gold-500/25'
      )}
    >
      <AudienceSymbol audience={audience} compact={inline} />
      {inline && (
        <span className="truncate leading-none">{compactLabel}</span>
      )}
    </button>
  )

  const message = (
    <>
      <p className="text-xs font-medium text-gold-700">{label}</p>
      <p className="mt-1 text-[11px] leading-relaxed text-zinc-400">
        {description}
      </p>
    </>
  )

  if (onImage) {
    return (
      <div
        ref={rootRef}
        className={cn('absolute inset-0 z-10 pointer-events-none', className)}
      >
        <div className="absolute left-3 bottom-3 pointer-events-auto">{button}</div>

        {open && (
          <div
            className="absolute inset-0 z-20 flex flex-col justify-end p-3 pointer-events-auto"
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              setOpen(false)
            }}
          >
            <div
              role="tooltip"
              className="mb-10 max-h-[calc(100%-3.25rem)] w-full overflow-y-auto rounded-xl border border-gold-500/25 bg-luxury-950/95 px-3 py-2.5 text-left shadow-xl backdrop-blur-sm"
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
              }}
            >
              {message}
            </div>
          </div>
        )}
      </div>
    )
  }

  if (inline) {
    return (
      <div ref={rootRef} className={cn('relative max-w-full shrink-0', className)}>
        {button}
        {open && (
          <div
            role="tooltip"
            className="absolute left-0 top-full z-20 mt-1.5 w-44 max-w-[min(100vw-2rem,11rem)] rounded-xl border border-gold-500/25 bg-white px-3 py-2.5 text-left shadow-lg"
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
            }}
          >
            <p className="text-xs font-medium text-gold-800">{label}</p>
            <p className="mt-1 text-[11px] leading-relaxed text-zinc-600">
              {description}
            </p>
          </div>
        )}
      </div>
    )
  }

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      {button}

      {open && (
        <div
          role="tooltip"
          className="absolute right-0 top-full z-20 mt-2 w-44 rounded-xl border border-gold-500/25 bg-luxury-950/95 px-3 py-2.5 text-left shadow-xl backdrop-blur-sm"
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
          }}
        >
          {message}
        </div>
      )}
    </div>
  )
}
